import React, { useState, useRef } from 'react';
import FlightInput from './FlightInput';
import CostEstimate from './CostEstimate';
import DetailedBreakdown from './DetailedBreakdown';
import ScrollIndicator from './ScrollIndicator';
import { FlightAnalysis } from '../types';
import { useCurrency } from '../contexts/CurrencyContext';

function Home() {
  const [flightData, setFlightData] = useState<FlightAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currency } = useCurrency();
  const widgetRef = useRef<HTMLDivElement>(null);

  const scrollToWidget = () => {
    widgetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleFlightSubmit = async (flightNumber: string, basePrice?: number) => {
    setLoading(true);
    setError(null);
    setFlightData(null);

    try {
      const requestBody: { flightNumber: string; basePrice?: number; currency?: string } = { 
        flightNumber,
        currency,
      };
      // Include basePrice if provided (even if 0, though that's unlikely to be valid)
      if (basePrice !== undefined) {
        requestBody.basePrice = basePrice;
      }

      // Use environment variable if set, otherwise use relative path (works with proxy)
      const apiUrl = process.env.REACT_APP_API_URL || '';
      let response: Response;
      const requestStartTime = Date.now();
      
      console.log('[Flight Request] Starting request for:', flightNumber, 'API URL:', apiUrl || '(using proxy)');
      
      try {
        const requestUrl = apiUrl ? `${apiUrl}/api/flight/analyze` : '/api/flight/analyze';
        console.log('[Flight Request] Fetching:', requestUrl);
        
        response = await fetch(requestUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });
        
        const requestDuration = Date.now() - requestStartTime;
        console.log('[Flight Request] Response received:', response.status, response.statusText, `(${requestDuration}ms)`);
      } catch (networkError) {
        console.error('[Flight Request] Network error caught:', networkError);
        // Network error (timeout, connection refused, etc.)
        const errorMessage = networkError instanceof Error ? networkError.message : 'Network error';
        
        // Check if it's a timeout error (which might be a 504)
        const isTimeout = errorMessage.toLowerCase().includes('timeout') || 
                         errorMessage.toLowerCase().includes('504') ||
                         errorMessage.toLowerCase().includes('gateway');
        const errorType = isTimeout ? 'GATEWAY_TIMEOUT' : 'NETWORK_ERROR';
        
        // Log to backend with better error handling
        // Use a timeout to prevent the logging request from hanging
        const logErrorToBackend = async () => {
          try {
            const logUrl = apiUrl ? `${apiUrl}/api/errors/client` : '/api/errors/client';
            console.log('[Error Logging] Attempting to log network error:', {
              url: logUrl,
              type: errorType,
              flightNumber,
            });
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
            
            const logResponse = await fetch(logUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                level: 'error',
                type: errorType,
                message: `Network error: ${errorMessage}`,
                flightNumber: flightNumber, // Also include at top level
                details: {
                  flightNumber: flightNumber, // Ensure it's in details
                  apiSource: isTimeout 
                    ? 'Backend API (Possible: Amadeus, AeroDataBox, or Database)'
                    : 'Backend API',
                  basePrice,
                  currency,
                  originalError: errorMessage,
                  url: apiUrl ? `${apiUrl}/api/flight/analyze` : '/api/flight/analyze',
                },
                statusCode: isTimeout ? 504 : 0,
              }),
              signal: controller.signal,
            });
            
            clearTimeout(timeoutId);
            
            if (!logResponse.ok) {
              console.warn('[Error Logging] Failed to log network error to backend:', logResponse.status, logResponse.statusText);
            } else {
              console.log('[Error Logging] Successfully logged network error');
            }
          } catch (logErr) {
            if (logErr instanceof Error && logErr.name === 'AbortError') {
              console.error('[Error Logging] Logging request timed out after 5 seconds');
            } else {
              console.error('[Error Logging] Error logging network error to backend:', logErr);
            }
          }
        };
        
        // Log asynchronously but don't wait for it
        logErrorToBackend();
        
        throw new Error(`Network error: ${errorMessage}. Please check your connection and try again.`);
      }

      if (!response.ok) {
        console.warn('[Flight Request] Response not OK:', response.status, response.statusText);
        
        // Try to parse error response, but handle empty responses
        let errorData;
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const text = await response.text();
          console.log('[Flight Request] Response text length:', text.length);
          if (text) {
            try {
              errorData = JSON.parse(text);
              errorMessage = errorData.error || errorMessage;
              console.log('[Flight Request] Parsed error data:', errorData);
            } catch {
              // If not JSON, use the text as error message
              errorMessage = text || errorMessage;
              console.log('[Flight Request] Using text as error message:', errorMessage.substring(0, 100));
            }
          }
        } catch (parseError) {
          // If we can't read the response, use status info
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          console.error('[Flight Request] Error reading response:', parseError);
        }
        
        // Determine error type - check both status code and message
        const is504 = response.status === 504 || errorMessage.includes('504') || errorMessage.toLowerCase().includes('gateway timeout');
        const errorType = is504 ? 'GATEWAY_TIMEOUT' : 'HTTP_ERROR';
        const requestDuration = Date.now() - requestStartTime;
        
        console.log('[Flight Request] Error type determined:', errorType, 'is504:', is504, 'status:', response.status);
        
        // Collect response headers for debugging
        const responseHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          responseHeaders[key] = value;
        });
        
        // Log HTTP errors (especially 504) to backend
        // Use a more robust logging approach that doesn't fail silently
        const logErrorToBackend = async () => {
          try {
            const logUrl = apiUrl ? `${apiUrl}/api/errors/client` : '/api/errors/client';
            // Use console.error so it shows even if console is filtered to errors only
            console.error('[ERROR LOGGING] Attempting to log HTTP error');
            console.error('[ERROR LOGGING] URL:', logUrl);
            console.error('[ERROR LOGGING] Type:', errorType);
            console.error('[ERROR LOGGING] Status:', response.status);
            console.error('[ERROR LOGGING] Flight:', flightNumber);
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout for logging
            
            // Determine which API was likely being called based on the request
            // For 504 errors, it's likely a timeout on the backend which could be any API
            const apiSource = response.status === 504 
              ? 'Backend API (Possible: Amadeus, AeroDataBox, or Database)'
              : 'Backend API';
            
            const logPayload = {
              level: 'error',
              type: errorType,
              message: errorMessage,
              flightNumber: flightNumber, // Also include at top level for easy access
              details: {
                flightNumber: flightNumber, // Ensure it's in details
                apiSource: apiSource,
                basePrice,
                currency,
                httpStatus: response.status,
                statusText: response.statusText,
                url: apiUrl ? `${apiUrl}/api/flight/analyze` : '/api/flight/analyze',
                requestBody: requestBody,
                requestDuration: requestDuration,
                requestStartTime: new Date(requestStartTime).toISOString(),
                responseHeaders: responseHeaders,
                userAgent: navigator.userAgent,
                language: navigator.language,
                platform: navigator.platform,
                onLine: navigator.onLine,
                connectionType: (navigator as any).connection?.effectiveType || 'unknown',
                timestamp: new Date().toISOString(),
              },
              statusCode: response.status,
            };
            
            console.error('[ERROR LOGGING] Payload:', JSON.stringify(logPayload, null, 2));
            
            const logResponse = await fetch(logUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(logPayload),
              signal: controller.signal,
            });
            
            clearTimeout(timeoutId);
            
            console.error('[ERROR LOGGING] Response status:', logResponse.status);
            
            if (!logResponse.ok) {
              const errorText = await logResponse.text();
              console.error('[ERROR LOGGING] FAILED to log:', logResponse.status, logResponse.statusText, errorText);
            } else {
              const result = await logResponse.json();
              console.error('[ERROR LOGGING] SUCCESS - logged error:', result);
            }
          } catch (logErr) {
            if (logErr instanceof Error && logErr.name === 'AbortError') {
              console.error('[ERROR LOGGING] TIMEOUT - Logging request timed out after 10 seconds');
            } else {
              console.error('[ERROR LOGGING] EXCEPTION - Error logging to backend:', logErr);
              console.error('[ERROR LOGGING] Exception details:', logErr instanceof Error ? logErr.message : String(logErr));
            }
          }
        };
        
        // Log asynchronously but don't wait for it
        logErrorToBackend();
        
        throw new Error(errorMessage);
      }

      // Parse response, handling empty responses
      const text = await response.text();
      if (!text) {
        throw new Error('Empty response from server');
      }
      const data = JSON.parse(text);
      
      // Debug logging to verify what we're receiving
      console.log('[Frontend] Received includedServices:', JSON.stringify(data.includedServices));
      console.log('[Frontend] Full response data:', JSON.stringify(data, null, 2));
      
      // Fallback validation: if basePrice is 0 and no manual price was provided, show error
      if (data.basePrice === 0 && basePrice === undefined) {
        throw new Error('Unable to fetch base price for this flight. Please provide the base price manually.');
      }
      
      setFlightData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      // Error logging is already handled in the try block above
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <div className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Flight Verdict</h1>
          <p className="hero-subtitle">How much does the cheaper ticket really cost?</p>
        </div>
        <div className="search-form-wrapper">
          <FlightInput onSubmit={handleFlightSubmit} loading={loading} />
          <ScrollIndicator show={!!flightData && !loading} />
        </div>
      </div>
      <main className="App-main">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {flightData && (
          <div className="results-container">
            <CostEstimate data={flightData} />
            <DetailedBreakdown data={flightData} />
            {/* <ComparisonView data={flightData} /> */}
          </div>
        )}

        <div className="flight-search-header">
          <button 
            className="find-flight-button"
            onClick={scrollToWidget}
          >
            Don't have a flight to consider yet? Find one now.
          </button>
        </div>

        {/* Whitelabelled Flight Search Widget */}
        <div ref={widgetRef}>
          <div id="tpwl-search"></div>
          <div id="tpwl-tickets"></div>
        </div>
      </main>
    </div>
  );
}

export default Home;

